import * as React from 'react';
import classNames from 'classnames';
import { timeline } from 'motion';
import { TimelineDefinition } from '@motionone/dom/types/timeline/types';
import { breakpoints } from '../../helpers/variables';
import SearchInput from './components/SearchInput';
import Facets from './components/Facets';
import FacetsApplied from './components/FacetsApplied';
import Pagination from './components/Pagination';
import ResultsCard from './components/ResultsCard';
import { useSearch } from './hooks';
import ResourceCard from './components/ResourceCard';
import BlogCard from './components/BlogCard';
import ProductCard from './components/ProductCard';


export interface AppProps {
  cardType: string;
  sortOrder: string;
  hasQuery?: string;
  placeholder?: string;
  itemsPerPage: string;
  labelHeading?: string;
  labelHeadingSummary?: string;
  labelSearchSummary: string;
  labelSortBy: string;
  labelFilter: string;
  labelFilterSort: string;
  labelFilterButton: string;
  labelFilterClear: string;
  labelSortRelevance: string;
  labelSortAlphabetical: string;
  labelSortRecent: string;
  labelNoResults: string;
}

const SearchApp: React.FC<AppProps> = ({
  cardType,
  sortOrder = 'relevance,recent,alphabetical',
  hasQuery,
  placeholder,
  itemsPerPage,
  labelHeading,
  labelHeadingSummary,
  labelSearchSummary,
  labelSortBy,
  labelFilter,
  labelFilterSort,
  labelFilterButton,
  labelFilterClear,
  labelSortRelevance,
  labelSortAlphabetical,
  labelSortRecent,
  labelNoResults
}) => {
  const {
    state: { loading, query, sort, resultsTotal, results },
    updateSort
  } = useSearch();
  const refEl = React.useRef<HTMLDivElement>(null);
  const refLoading = React.useRef<SVGSVGElement>(null);
  const [isDesktop, setIsDesktop] = React.useState(false);
  const [isFasetPanelOpen, setIsFasetPanelOpen] = React.useState(false);
  const breakpoint = window.matchMedia(`(min-width: ${breakpoints.desktop}px)`);

  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(breakpoint.matches);
    };

    handleResize();
    breakpoint.addEventListener('change', handleResize);

    return () => {
      breakpoint.removeEventListener('change', handleResize);
    };
  }, []);

  React.useEffect(() => {
    const paths = refLoading.current?.querySelectorAll('path');

    if (paths) {
      const sequence: TimelineDefinition = [];

      paths.forEach(path => {
        sequence.push([
          path,
          { scale: [0, 1] },
          { duration: 1, at: '-0.2', easing: 'ease-in' }
        ]);
      });

      timeline(sequence, {
        duration: 1.5,
        endDelay: 2,
        direction: 'alternate',
        repeat: Infinity
      });
    }
  }, [refLoading.current, loading]);

  const handlePageChange = () => {
    if (refEl.current) {
      refEl.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getSortOptions = React.useCallback(() => {
    const sortOrderList = sortOrder.split(',');
    const options = [
      {
        value: 'relevance',
        label: `${labelSortBy} ${labelSortRelevance}`
      },
      {
        value: 'alphabetical',
        label: `${labelSortBy} ${labelSortAlphabetical}`
      },
      {
        value: 'recent',
        label: `${labelSortBy} ${labelSortRecent}`
      }
    ];

    return options
      .sort(
        (a, b) =>
          sortOrderList.indexOf(a.value.trim()) -
          sortOrderList.indexOf(b.value.trim())
      )
      .map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ));
  }, []);

  return (
    <div ref={refEl}>
      {labelHeading && (
        <div className="search__header">
          <h1 className="search__heading">{labelHeading}</h1>
          {(query || (!loading && query)) && (
            <p className="search__subheading">
              {labelHeadingSummary
                ?.replace('[keyword]', query)
                .replace('[total]', resultsTotal.toString())}
            </p>
          )}
        </div>
      )}

      {hasQuery === 'true' && <SearchInput placeholder={placeholder} />}

      <div className="search__container">
        <Facets
          title={labelFilter}
          buttonLabel={labelFilterButton}
          clearLabel={labelFilterClear}
          labelSortBy={labelSortBy}
          labelSortRelevance={labelSortRelevance}
          labelSortAlphabetical={labelSortAlphabetical}
          labelSortRecent={labelSortRecent}
          isDesktop={isDesktop}
          isOpen={isFasetPanelOpen}
          isLoading={loading}
          onClose={() => setIsFasetPanelOpen(false)}
        />

        <div className="search__results">
          {!loading && (
            <>
              <div className="search__results-top">
                <p className="search__results-count">
                  {labelSearchSummary
                    .replace('[count]', results.length.toString())
                    .replace('[total]', resultsTotal.toString())}
                </p>
                {isDesktop ? (
                  <select
                    className="dropdown"
                    value={sort}
                    onChange={e => updateSort(e.target.value)}
                  >
                    {getSortOptions()}
                  </select>
                ) : (
                  <button
                    className="dropdown dropdown--sort"
                    onClick={() => setIsFasetPanelOpen(true)}
                  >
                    {labelFilterSort}
                  </button>
                )}
              </div>

              <FacetsApplied clearLabel={labelFilterClear} />

              {!results.length && query && (
                <div
                  className="search__no-results"
                  dangerouslySetInnerHTML={{
                    __html: labelNoResults.replace('[keyword]', query)
                  }}
                ></div>
              )}

              {!!results.length && (
                <>
                  <div
                    className={classNames([
                      'search__results-list',
                      { 'grid grid--33-33-33': cardType === 'resource' },
                      { 'grid grid--50-50': cardType === 'product' },
                      { '': cardType === 'landing' }
                    ])}
                  >
                    {results.map(result =>
                      cardType === 'resource' ? (<ResourceCard key={result.id} {...result} />) : cardType === 'landing' ?(<BlogCard key={result.id} {...result} />) : cardType === 'product' ? (<ProductCard key={result.id} {...result} />)
                      : 
                      ((<ResultsCard key={result.id} {...result} />))
                    )}
                    
                  </div>
                  <Pagination
                    itemsPerPage={parseInt(itemsPerPage)}
                    onPaginate={handlePageChange}
                  />
                </>
              )}
            </>
          )}

          {loading && (
            <div className="search__loading">
              <svg
                viewBox="0 0 75 59"
                xmlns="http://www.w3.org/2000/svg"
                ref={refLoading}
              >
                <path d="M11.9077 58.0077H0L42.6923 0.938477V16.4462L11.9077 58.0077Z" />
                <path d="M27.5314 58.0077H15.6468L42.6929 22.0308V37.5385L27.5314 58.0077Z" />
                <path d="M43.2923 58.0077H31.4077L74.0538 0.938477V16.4462L43.2923 58.0077Z" />
                <path d="M58.9385 58.0077H47.0539L74.0539 22.0308V37.5385L58.9385 58.0077Z" />
                <path d="M74.0538 58.0077H61.9384L74.0538 42.3846V58.0077Z" />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchApp;